'use client';
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { IMessage, useMessageContext } from '@ui/components/organisms/message';
import { ContextUserType } from '@ui/constants/context';
import PostItem from './post-item';
import { CommentList, ReplyFormPortal, Separator } from '@ui/components';
import { ReplyRefProvider } from '@ui/contexts';
import { PostItemSkeleton } from './post-item-skeleton';
import { MessageUpdateFormPortal } from '../common/message-update-form-portal';
import { MessageUpdateFormProvider } from '@ui/contexts/message-update-form-context';
import { DynamicRouteBuilder } from '@ui/constants';

interface Props {
  contextUserType: ContextUserType;
  message: IMessage;
  isLoadingSingleMessage: boolean;
  isSingleMessageError: boolean;
  singleMessageError: any;
  useGetMessageByIdQuery: any;
  creatorData?: { platform_settings?: { enable_video_uploads?: boolean } };
  moderators_ids: string[];
}

export const PostViewV2 = (props: Props) => {
  const searchParams = useSearchParams();
  const messageState = useMessageContext(props.contextUserType);
  const scrollId = useSearchParams().get('scroll');
  const commentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollId && props.message) {
      const timer = setTimeout(() => {
        const commentElement = commentRef.current;
        if (commentElement) {
          commentElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [scrollId, props.message]);

  const { data: rootPostData } = props.useGetMessageByIdQuery(props.message?.main_post?.id, {
    skip: !props.message.main_post?.id,
  });

  // if reply, get the comment
  const { data: parentPostData } = props.useGetMessageByIdQuery(props.message.parent_post?.id, {
    skip: !props.message.parent_post?.id && props.message.thread_deepness === 2,
  });

  const rootPost = useMemo(() => rootPostData as IMessage, [rootPostData]);
  const comment = useMemo(
    () => (props.message.thread_deepness === 2 ? (parentPostData as IMessage) : props.message),
    [props.message, parentPostData],
  );

  const parentThreadRoute = useMemo(() => {
    const baseRoute = messageState.communityPostRoute;

    if (props.contextUserType === 'fan') {
      const route = DynamicRouteBuilder.postBySlug(
        props.message.community_details?.creator_display_name || props.message.creator_display_name,
        props.message.conversation_id,
        props.message.main_post?.slug as string,
      );
      return route;
    } else {
      const params = new URLSearchParams(searchParams);
      params.set('slug', props.message.main_post?.id as string);
      return `${baseRoute}/?${params.toString()}`;
    }
  }, [props.contextUserType, searchParams, props.message.main_post?.id]);

  if (props.isLoadingSingleMessage) {
    return (
      <div className="mx-auto flex w-full max-w-[876px]">
        <PostItemSkeleton />
      </div>
    );
  }

  if (props.isSingleMessageError) {
    return (
      <div className="m-auto max-w-sm">
        <div className="flex flex-col justify-center space-y-4">
          <div className="text-center">
            <p className="font-nunito text-korDarkGrey text-3xl font-extrabold">
              {props.singleMessageError?.status === 403
                ? 'Oops, post is restricted.'
                : props.singleMessageError?.status === 422
                ? 'Content Removed'
                : 'Something went wrong'}
            </p>
            <p className="font-nunito text-korDarkGrey/40">
              {props.singleMessageError?.status === 422
                ? 'This content is no longer available.'
                : props.singleMessageError?.data?.error || 'Error loading post...'}
            </p>
          </div>
          <Separator className="" />
        </div>
      </div>
    );
  }

  if (!props.message) {
    return <div>No post found</div>;
  }

  return (
    <MessageUpdateFormProvider>
      <ReplyRefProvider>
        <>
          <MessageUpdateFormPortal
            userType={props.contextUserType}
            main_post_ref={props.message.main_post?.id as string}
          />

          <div className="mx-auto flex w-full max-w-[876px] flex-col gap-4 pb-20">
            {/* root post */}
            {rootPost && (
              <PostItem
                key={rootPost?.id}
                post={rootPost}
                userType={props.contextUserType}
                userId={messageState.user_id}
                creator_id={rootPost.creator_id}
                communityPostRoute={messageState.communityPostRoute}
                isOwnPage
                query={messageState.query}
                contentType="post"
                isDynamicRoute={false}
                communityDetails={
                  props.message.community_details as NonNullable<IMessage['community_details']>
                }
                moderators_ids={props.moderators_ids}
              />
            )}

            {comment && (
              <div className="flex items-center gap-4">
                <Separator className="flex-auto bg-primaryActive" />
                <a href={parentThreadRoute}>
                  <p className="text-xs whitespace-nowrap text-primaryActive">See all comments</p>
                </a>
                <Separator className="flex-auto bg-primaryActive" />
              </div>
            )}

            {/* {!rootPost?.draft && <p className="font-nunito text-sm font-semibold">Comments</p>} */}

            {comment && (
              <ReplyFormPortal
                userType={props.contextUserType}
                creator_id={comment.creator_id}
                user_id={messageState.user_id}
                main_post_ref={rootPost?.id}
                queryParams={messageState.query}
                community_details={
                  comment.community_details as NonNullable<IMessage['community_details']>
                }
              />
            )}

            {rootPost && !rootPost?.draft && comment && (
              <ReplyRefProvider>
                <CommentList
                  contentType="comment"
                  userType={props.contextUserType}
                  post={rootPost}
                  creator_id={rootPost.community_details?.creator_id as string}
                  user_id={messageState.user_id}
                  main_post_ref={rootPost?.id}
                  enableReply={true}
                  isDynamicRoute={false}
                  filter_by_comment_id={comment.id}
                  {...(props.message.thread_deepness === 2 && {
                    filter_by_reply_id: props.message.id,
                  })}
                  creatorData={props.creatorData}
                  moderators_ids={props.moderators_ids || []}
                />
              </ReplyRefProvider>
            )}
          </div>
        </>
      </ReplyRefProvider>
    </MessageUpdateFormProvider>
  );
};
